/**
 * Timezone-aware date helpers.
 *
 * Storage is always UTC. Presentation and "local calendar date" logic uses the
 * user's timezone (default Asia/Jakarta) via Intl, so DST-capable zones work
 * without hard-coded UTC offsets.
 */

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = partsFormatterCache.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    partsFormatterCache.set(timeZone, fmt);
  }
  return fmt;
}

export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = getFormatter(timeZone).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // Intl may return hour "24" for midnight in some environments; normalize.
  const hour = get('hour') % 24;
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  };
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/** Local calendar date for a UTC instant, formatted YYYY-MM-DD. */
export function localDate(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

/** Local time for a UTC instant, formatted HH:mm. */
export function localTime(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Day of week for a local date string (0 = Sunday .. 6 = Saturday). */
export function dayOfWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Add days to a YYYY-MM-DD string. */
export function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${pad(dt.getUTCFullYear(), 4)}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** Monday (YYYY-MM-DD) of the week containing the given local date. */
export function startOfWeek(dateStr: string): string {
  const dow = dayOfWeek(dateStr);
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDays(dateStr, diff);
}

/**
 * Convert a local wall-clock date+time in a timezone to a UTC Date.
 * Uses iterative offset resolution so DST transitions are handled.
 */
export function zonedTimeToUtc(dateStr: string, timeStr: string, timeZone: string): Date {
  const [y, mo, d] = dateStr.split('-').map(Number) as [number, number, number];
  const [h, mi] = timeStr.split(':').map(Number) as [number, number];
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi, 0, 0);
  // First pass: offset at the guess.
  const off1 = tzOffsetMs(new Date(utcGuess), timeZone);
  const candidate1 = utcGuess - off1;
  // Second pass: offset at the candidate (handles DST boundaries).
  const off2 = tzOffsetMs(new Date(candidate1), timeZone);
  return new Date(utcGuess - off2);
}

/** Offset (ms) of a timezone at a given instant (local - UTC). */
export function tzOffsetMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Compare against the UTC instant truncated to seconds.
  const truncated = Math.floor(date.getTime() / 1000) * 1000;
  return asUtc - truncated;
}

/** 00:00 of the local date as a UTC Date. */
export function startOfLocalDay(dateStr: string, timeZone: string): Date {
  return zonedTimeToUtc(dateStr, '00:00', timeZone);
}

/** 23:59:59.999 of the local date as a UTC Date. */
export function endOfLocalDay(dateStr: string, timeZone: string): Date {
  return zonedTimeToUtc(dateStr, '23:59', timeZone);
}

export function nowUtcIso(): string {
  return new Date().toISOString();
}

export function greetingFor(hour: number): string {
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Good night';
}

export function formatMinutes(total: number): string {
  if (total <= 0) return '0m';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function formatDateHuman(dateStr: string, timeZone?: string): string {
  void timeZone;
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
